<template>
  <v-card color="card" class="elevation-2 rounded-lg">
    <v-sheet color="primary">
      <v-card-title class="pa-2">
        <div class="text-h6 ml-2 white--text">
          {{ getLabel('restrictions') }}
        </div>
      </v-card-title>
    </v-sheet>
    <v-card-text>
      <div style="max-height:500px;" class="overflow-y-auto ">
        <RestrictionCard
          v-for="(restriction, index) in restrictionsToShow"
          :key="index"
          class="my-4 mx-3"
          :remove-restriction="() => showDialog(index)"
          :edit-restriction="() => editRestriction(index)"
          :restriction="restriction"
          :edit-mode="true"
        />
        <div v-if="restrictionsToShow.length === 0" class="text-center text-body-1">{{ getLabel('no_restrictions') }}</div>
      </div>
      <div class="pt-4 text-center">
        <v-btn color="primary" large @click="goCreatePermission()">{{ getLabel('create_restriction') }}</v-btn>
      </div>
      <v-dialog
        v-model="dialog"
        max-width="1000"
      >
        <v-card>
          <v-sheet dark color="grey">
            <v-card-title class="text-h5">
              {{ getLabel("delete_restriction_question") }}
            </v-card-title>
          </v-sheet>
          <v-card-text class="py-6">
            <RestrictionCard v-if="restrictionToDelete" :restriction="restrictionToDelete" :edit-mode="false" />
          </v-card-text>
          <v-divider />
          <v-card-actions class="pr-4 py-4">
            <v-spacer />
            <v-btn
              color="red darken-1"
              dark
              large
              @click="dialog = false"
            >
              <v-icon class="mr-2">mdi-close-thick</v-icon>

              {{ getLabel("no") }}
            </v-btn>
            <v-btn
              class="ml-4"

              color="green darken-1"
              large
              dark
              @click="removeRestriction()"
            >
              <v-icon class="mr-2">mdi-check-bold</v-icon>
              {{ getLabel("yes") }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card-text>
  </v-card>
</template>

<script>
import { ref, computed } from '@vue/composition-api';
import { useRouter, useRoute } from 'vue2-helpers/vue-router';
import { useMutation } from '@vue/apollo-composable';

import { useMainStore } from '@/store/index';
import { getGraphqlSchema } from '@/graphql/index';
import { getLabel, EventBus } from '@/utils/index';

import RestrictionCard from '@/components/UI/RestrictionCard.vue';

export default {
  components: { RestrictionCard },
  setup () {
    const router = useRouter();

    const store = useMainStore();

    const route = useRoute();

    const object = route.name === 'manageProfile' ? 'collaborator' : 'group';

    const dialog = ref(false);

    const index = ref(null);

    const showDialog = indexToDelete => {
      index.value = indexToDelete;
      dialog.value = true;
    };

    const restrictionsToSave = ref(null);

    const { workspaces } = store;

    const restrictionToDelete = computed(() => {
      if (ref(store[object].restrictions).value[index.value]) {
        return ref(store[object].restrictions).value[index.value];
      }
      return null;
    });

    const restrictionsToShow = computed(() => {
      const restrictions = [...ref(store[object].restrictions).value];

      return restrictions || [];
    });

    // Mutation to update collaborator restrictions
    const { mutate: mutationUpdateCollaboratorRestriction, onError: errorUpdateCollaborator, onDone: doneUpdateCollaborator } = useMutation(
      getGraphqlSchema('updateCollaboratorRestriction'),
      () => ({
        variables:
    {
      uuid: store.collaborator.uuid,
      restrictions: restrictionsToSave.value

    }
      })
    );

    errorUpdateCollaborator(() => {
      store.loading = false;

      EventBus.$emit('show-notifications', [{
        message: getLabel('error_deleting_restriction'),
        type: 'danger'
      }]);
    });

    doneUpdateCollaborator(result => {
      store.collaborator.restrictions = result.data.updateCollaborator.restrictions;
      restrictionsToSave.value = null;

      EventBus.$emit('show-notifications', [{
        message: getLabel('restriction_deleted'),
        type: 'success'
      }]);

      store.loading = false;
    });

    /** *********************************************** */

    // mutation to Update Restrictions
    const { mutate: mutationUpdateGroupRestriction, onError: errorUpdateGroup, onDone: doneUpdateGroup } = useMutation(
      getGraphqlSchema('updateGroupRestriction'),
      () => ({
        variables:
    {
      uuid: store.group.uuid,
      restrictions: restrictionsToSave.value

    }
      })
    );

    errorUpdateGroup(() => {
      store.loading = false;

      EventBus.$emit('show-notifications', [{
        message: getLabel('error_deleting_restriction'),
        type: 'danger'
      }]);
    });

    doneUpdateGroup(result => {
      store.group.restrictions = result.data.updateGroup.restrictions;
      restrictionsToSave.value = null;

      EventBus.$emit('show-notifications', [{
        message: getLabel('restriction_deleted'),
        type: 'success'
      }]);

      EventBus.$emit('update-group', {
        uuid: store.group.uuid,
        restrictions_count: store.group.restrictions.length
      });

      store.loading = false;
    });
    /** *********************************************** */

    const removeRestriction = () => {
      dialog.value = false;

      restrictionsToSave.value = [...store[object].restrictions];
      restrictionsToSave.value.splice(index.value, 1);

      index.value = null;

      store.loading = true;

      if (object === 'collaborator') {
        mutationUpdateCollaboratorRestriction();
      } else {
        mutationUpdateGroupRestriction();
      }
    };

    const editRestriction = indexToEdit => {
      if (object === 'collaborator') {
        router.push({ name: 'editPermission', params: { uuid: store.collaborator.uuid, index: indexToEdit } });
      } else {
        router.push({ name: 'editGroupPermission', params: { uuid: store.group.uuid, index: indexToEdit } });
      }
    };

    const goCreatePermission = () => {
      if (object === 'collaborator') {
        router.push({ name: 'newPermission', params: { uuid: store[object].uuid } });
      } else {
        router.push({ name: 'newGroupPermission', params: { uuid: store.group.uuid } });
      }
    };

    return {
      dialog,
      index,
      showDialog,
      workspaces,
      getLabel,
      restrictionToDelete,
      restrictionsToShow,
      removeRestriction,
      editRestriction,
      goCreatePermission
    };
  }
};
</script>
